/**
 * Account & sign-in rules (Settings → Account & sign-in; docs/features/account-sign-in.md), the same on the server
 * (what it refuses) and every client (what it offers):
 *
 * - Ways in: email codes (to the primary or any verified secondary address), a password, passkeys and linked
 *   providers (Google, Apple). One of them can be removed only while another remains. Secondary addresses can always
 *   be removed (the primary address keeps email codes working); the primary one is changed, never removed.
 * - Proof for sensitive changes ("step-up"): changing or removing a password needs the current one; setting a first
 *   password needs an email code; turning two-step verification on or off and new recovery codes need the password
 *   when there is one, else an email code. Everything else needs only the signed-in session.
 * - Sign-in: email code, password, or a provider; then, when two-step verification is on, an authenticator or
 *   backup code ("trust this device" skips it for 30 days). A password reset doesn't skip it: the reset ends with a
 *   password sign-in, which asks for the second step. A passkey signs in on its own (it is both factors, as Google treats it).
 */
import type { AccountEmail, AccountResponse } from "@bundle/contracts";

/** Passwords: at least 8 characters (NIST SP 800-63B), at most 128. */
export const PASSWORD_MIN = 8;
export const PASSWORD_MAX = 128;
/** Backup codes issued when two-step verification is turned on (or regenerated); each works once. */
export const BACKUP_CODE_COUNT = 10;
/** "Trust this device" after a two-step challenge (ticked by default). */
export const TRUST_DEVICE_DAYS = 30;
/** Password sign-in locks after this many wrong passwords for one address within the window, for the lock time. */
export const LOCKOUT_FAILURES = 10;
export const LOCKOUT_WINDOW_MIN = 15;
export const LOCKOUT_MIN = 15;

/** How an account can sign in. */
export type SignInState = {
  /** Email codes reach the primary address (the deployment sends email; the address is a real, verified one). */
  emailCode: boolean;
  password: boolean;
  passkeys: number;
  /** Linked Google / Apple accounts. */
  providers: number;
};

export type RemovableMethod = "password" | "passkey" | "provider";

/** The ways in of an account (GET /v1/account). Older servers send no security fields: none counted. */
export function signInStateOf(account: AccountResponse): SignInState {
  return {
    emailCode: account.emailCode ?? account.providers.includes("email"),
    password: account.hasPassword === true,
    passkeys: account.passkeys?.length ?? 0,
    providers: account.providers.filter((p) => p !== "email").length,
  };
}

/** How many ways in (each passkey and provider counts once). */
export function methodTotal(s: SignInState): number {
  return (s.emailCode ? 1 : 0) + (s.password ? 1 : 0) + s.passkeys + s.providers;
}

/** The account has a method of this kind and another way in would remain without it. */
export function canRemoveMethod(s: SignInState, kind: RemovableMethod): boolean {
  const has = kind === "password" ? s.password : kind === "passkey" ? s.passkeys > 0 : s.providers > 0;
  return has && methodTotal(s) > 1;
}

/** A secondary address can be removed; the primary one can't (change it instead). */
export function canRemoveEmail(emails: AccountEmail[], id: string): boolean {
  return emails.some((e) => e.id === id && !e.primary);
}

/** A verified secondary address can become the primary one. */
export function canMakePrimary(emails: AccountEmail[], id: string): boolean {
  return emails.some((e) => e.id === id && !e.primary && e.verified);
}

/** Primary first, then verified secondary addresses, then ones waiting for their code; oldest first within each. */
export function sortEmails(emails: AccountEmail[]): AccountEmail[] {
  const rank = (e: AccountEmail) => (e.primary ? 0 : e.verified ? 1 : 2);
  return emails.toSorted((a, b) => rank(a) - rank(b) || a.addedAt - b.addedAt || a.email.localeCompare(b.email));
}

export type StepUpAction =
  | "setPassword"
  | "changePassword"
  | "removePassword"
  | "enableTwoFactor"
  | "disableTwoFactor"
  | "regenerateRecoveryCodes"
  | "addEmail"
  | "removeEmail"
  | "makePrimaryEmail"
  | "changePrimaryEmail"
  | "addPasskey"
  | "renamePasskey"
  | "removePasskey"
  | "unlinkProvider"
  /** Add, change or remove my phone number (the server's requireStepUp). */
  | "changePhone";

/** What the person must show for a change: nothing more than the session, the current password, or an email code. */
export type StepUp = "none" | "password" | "emailCode";

export function stepUpFor(action: StepUpAction, hasPassword: boolean, freshSignup = false): StepUp {
  switch (action) {
    case "setPassword":
      // Right after Create account the sign-up code proved the address (AccountResponse.freshSignup); nothing else skips.
      return hasPassword ? "password" : freshSignup ? "none" : "emailCode";
    case "changePassword":
    case "removePassword":
      return "password";
    case "enableTwoFactor":
    case "disableTwoFactor":
    case "regenerateRecoveryCodes":
      return hasPassword ? "password" : "emailCode";
    case "changePhone":
      // The session that just created the account adds its number without more proof (sign-up's phone step).
      return freshSignup ? "none" : hasPassword ? "password" : "emailCode";
    default:
      return "none";
  }
}

/**
 * Why a new password is refused. Length and "contains the email" are checked here (live hints on every client);
 * "tooCommon" (zxcvbn-ts dictionaries) and "leaked" (Have I Been Pwned, k-anonymity) only on the server.
 */
export type PasswordProblem = "tooShort" | "tooLong" | "containsEmail" | "tooCommon" | "leaked";

/** The local part of the address (or the whole address) inside the password, ignoring case. */
export function passwordContainsEmail(password: string, email: string | null | undefined): boolean {
  const e = (email ?? "").trim().toLowerCase();
  if (e === "") return false;
  const p = password.toLowerCase();
  const local = e.split("@")[0] ?? "";
  return p.includes(e) || (local.length >= 3 && p.includes(local));
}

/** The rules a client can check as the person types (no composition rules, per NIST). */
export function passwordProblem(password: string, email?: string | null): PasswordProblem | undefined {
  const length = [...password].length;
  if (length < PASSWORD_MIN) return "tooShort";
  if (length > PASSWORD_MAX) return "tooLong";
  if (passwordContainsEmail(password, email)) return "containsEmail";
  return undefined;
}

export type PasswordStrength = "weak" | "fair" | "strong";

/**
 * The 3-segment meter under a new password (sign-in reset, Settings). Anything passwordProblem refuses is weak; long
 * passwords and 12+ characters mixing three kinds are strong; few distinct characters ("aaaaaaaa") stay weak. Only a
 * hint: the server's zxcvbn-ts and leaked-password checks decide.
 */
export function passwordStrength(password: string, email?: string | null): PasswordStrength {
  if (passwordProblem(password, email) !== undefined) return "weak";
  const length = [...password].length;
  if (new Set(password.toLowerCase()).size < 4) return "weak";
  const kinds = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((r) => r.test(password)).length;
  if (length >= 16 || (length >= 12 && kinds >= 3)) return "strong";
  return "fair";
}

/** The live hints under a new password: met (true), not met (false), or only the server knows (undefined). */
export type PasswordHints = { length: boolean; email: boolean; common: boolean | undefined };

/** `refused`: the server's last refusal for this password (tooCommon / leaked turn the "common" hint off). */
export function passwordHints(password: string, email?: string | null, refused?: PasswordProblem): PasswordHints {
  const length = [...password].length;
  return {
    length: length >= PASSWORD_MIN && length <= PASSWORD_MAX,
    email: password !== "" && !passwordContainsEmail(password, email),
    common: refused === "tooCommon" || refused === "leaked" ? false : undefined,
  };
}

/** A server password refusal code (PASSWORD_TOO_SHORT …, any case) → the rule that failed. */
export function passwordProblemFor(code: string | null | undefined): PasswordProblem | undefined {
  switch ((code ?? "").toLowerCase()) {
    case "password_too_short":
      return "tooShort";
    case "password_too_long":
      return "tooLong";
    case "password_contains_email":
      return "containsEmail";
    case "password_too_common":
      return "tooCommon";
    case "password_leaked":
    case "password_compromised":
      return "leaked";
    default:
      return undefined;
  }
}

export type SecondFactor = "totp" | "otp";

/** Where a sign-in call left the person. */
export type SignInStep = { step: "done" } | { step: "twoFactor"; methods: SecondFactor[] };

/**
 * A Better Auth sign-in response (email code, password, provider ID token) → done, or the second step
 * (`{ twoFactorRedirect: true, twoFactorMethods }`; recovery codes are always allowed then).
 */
export function signInStepOf(body: unknown): SignInStep {
  const b = body as { twoFactorRedirect?: unknown; twoFactorMethods?: unknown } | null | undefined;
  if (b?.twoFactorRedirect !== true) return { step: "done" };
  const methods = Array.isArray(b.twoFactorMethods) ? b.twoFactorMethods.filter((m): m is SecondFactor => m === "totp" || m === "otp") : [];
  return { step: "twoFactor", methods: methods.length > 0 ? methods : ["totp"] };
}

/** The secret of an `otpauth://totp/…?secret=…` URI (for "enter this key" instead of the QR code). */
export function totpSecretOf(uri: string): string | undefined {
  if (!uri.toLowerCase().startsWith("otpauth://totp/")) return undefined;
  const q = uri.indexOf("?");
  if (q < 0) return undefined;
  for (const part of uri.slice(q + 1).split("&")) {
    const [k, v] = part.split("=");
    if (k?.toLowerCase() === "secret" && v) return decodeURIComponent(v).toUpperCase();
  }
  return undefined;
}

/** A TOTP secret in groups of four ("JBSW Y3DP EHPK 3PXP") for reading off a screen. */
export function groupSecret(secret: string): string {
  return (secret.replace(/\s+/g, "").toUpperCase().match(/.{1,4}/g) ?? []).join(" ");
}

/** What a person types as a code: digits and letters kept, spaces and dashes dropped. */
export function normalizeCode(input: string): string {
  return input.replace(/[\s-]+/g, "").trim();
}

export type SecurityError =
  | "wrongPassword"
  | "locked"
  | "invalidCode"
  | "challengeExpired"
  | "passwordTooShort"
  | "passwordTooLong"
  | "passwordContainsEmail"
  | "passwordTooCommon"
  | "passwordLeaked"
  | "passwordExists"
  | "emailCodeRequired"
  | "passwordRequired"
  | "lastMethod"
  | "emailInUse"
  | "invalidEmail"
  | "signInAgain"
  | "rateLimited"
  | "linkExpired"
  | "network"
  | "failed";

/** A server failure code (Better Auth's `code` or a /v1 error code, any case) → the message to show. */
export function securityErrorFor(code: string | null | undefined): SecurityError {
  switch ((code ?? "").toLowerCase()) {
    case "invalid_email_or_password":
    case "invalid_password":
    case "credential_account_not_found":
      return "wrongPassword";
    case "too_many_attempts":
    case "account_temporarily_locked":
      return "locked";
    case "invalid_code":
    case "invalid_backup_code":
    case "invalid_otp":
    case "otp_expired":
    case "otp_has_expired":
      return "invalidCode";
    case "invalid_two_factor_cookie":
    case "too_many_attempts_request_new_code":
      return "challengeExpired";
    case "password_too_short":
      return "passwordTooShort";
    case "password_too_long":
      return "passwordTooLong";
    case "password_contains_email":
      return "passwordContainsEmail";
    case "password_too_common":
      return "passwordTooCommon";
    case "password_leaked":
    case "password_compromised":
      return "passwordLeaked";
    case "password_exists":
      return "passwordExists";
    case "email_code_required":
      return "emailCodeRequired";
    case "password_required":
      return "passwordRequired";
    case "last_sign_in_method":
    case "failed_to_unlink_last_account":
      return "lastMethod";
    case "email_in_use":
    case "user_already_exists":
      return "emailInUse";
    case "invalid_email":
      return "invalidEmail";
    case "session_not_fresh":
    case "session_expired":
      return "signInAgain";
    case "rate_limited":
    case "too_many_requests":
      return "rateLimited";
    case "link_expired":
      return "linkExpired";
    case "network":
      return "network";
    default:
      return "failed";
  }
}
